/*
 * Copyright (c) 2025 - 2026 Bolmate B.V.
 */
import './style/bottomNavBar.scss';

import React, {useContext, useState} from 'react';
import {NavLink, useNavigate} from 'react-router';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import tileIcon from '@iconify/icons-ls/tile';
import Bell from '@iconify/icons-mdi/bell-outline';
import starIcon from '@iconify/icons-bi/star-fill';
import signOut from '@iconify/icons-fa/sign-out';
import userCircle from '@iconify/icons-fa-solid/user-circle';
import classNames from 'classnames';
import {DASHBOARD_ROUTE, NOTIFICATIONS_ROUTE, ORDERS_ROUTE, REVIEW_ANALYTICS_ROUTE} from '../../util/constants';
import {requestPageChangePermission} from '../../util/pageChanger';
import {AppContext} from '../misc/appContext';
import {useMobileNav} from '../misc/mobileNavContext';
import {useNotificationBadges} from '../misc/notificationBadgeContext';
import {BolmateBadge} from '../controls';
import {AppLanguage} from '../../i18n/config';
import {getCurrentLanguage, setUserLanguage} from '../../i18n/languageService';
import {onLogout} from '../../util/events';
import ContextAccountController from '../account/select/contextAccountController';
import FeedbackButton from '../feedback/feedbackButton';
import FeedbackDialogController from '../feedback/feedbackDialogController';
import SidebarMenuController from '../sidebarMenu/sidebarMenuController';

// Keep shortcuts in public/manifest.webmanifest in sync with the tabs below.
export default function BottomNavBar() {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {account} = context.accountContext;
    const badgeCounts = useNotificationBadges();
    const {menuOpen, moreOpen, toggleMore, closeAll} = useMobileNav();
    const [feedbackOpen, setFeedbackOpen] = useState(false);

    function renderBadge(route: string) {
        const count = badgeCounts[route];
        if (!count) return null;
        return (
            <BolmateBadge
                label={count > 99 ? '99+' : String(count)}
                color='red'
                size='small'
                className='bottom-nav-unread-badge'
            />
        );
    }

    function onClickTab(route: string) {
        return function (e: React.MouseEvent) {
            e.preventDefault();
            closeAll();
            requestPageChangePermission(() => {
                navigate(route);
            });
        };
    }

    function onClickLanguage(lang: AppLanguage) {
        return function () {
            void i18n.changeLanguage(lang);
            void setUserLanguage(user, lang);
        };
    }

    function onClickFeedback() {
        closeAll();
        setFeedbackOpen(true);
    }

    function onCloseFeedback() {
        setFeedbackOpen(false);
    }

    function onClickLogout() {
        closeAll();
        requestPageChangePermission(onLogout);
    }

    const currentLang = getCurrentLanguage();

    return (
        <>
            {(moreOpen || menuOpen) && <div className='bottom-nav-overlay' onClick={closeAll} />}
            {/* Sits OUTSIDE the bar so the bar (higher z-index) paints over it: the menu
                slides up from behind the bar instead of overlapping it during the animation. */}
            <div className={classNames('bottom-nav-more-menu', moreOpen && 'open')}>
                {user && (
                    <div className='more-menu-user'>
                        <Icon icon={userCircle} className='more-menu-user-icon' />
                        <label className='more-menu-user-name'>{user.fullName}</label>
                        {account !== null && (
                            <FeedbackButton onClick={onClickFeedback} className='more-menu-feedback-btn' />
                        )}
                    </div>
                )}
                <div className='more-menu-section'>
                    <label className='more-menu-section-title'>{t('header.salesAccount', 'Verkoopaccount')}</label>
                    <ContextAccountController label={null} itemsPosition='top' />
                </div>
                <div className='more-menu-section'>
                    <label className='more-menu-section-title'>{t('settings.language.label', 'Taal')}</label>
                    <div className='language-toggle'>
                        <button
                            className={classNames('lang-btn', currentLang === 'nl' && 'active')}
                            onClick={onClickLanguage('nl')}
                        >
                            <span className='fi fi-nl' /> NL
                        </button>
                        <button
                            className={classNames('lang-btn', currentLang === 'en' && 'active')}
                            onClick={onClickLanguage('en')}
                        >
                            <span className='fi fi-gb' /> EN
                        </button>
                    </div>
                </div>
                <div className='more-menu-item logout' onClick={onClickLogout}>
                    <Icon icon={signOut} className='more-menu-icon' />
                    <label>{t('header.signOut', 'Uitloggen')}</label>
                </div>
            </div>
            <nav className='bottom-nav-bar'>
                <div className={classNames('bottom-nav-menu-panel', menuOpen && 'open')}>
                    <SidebarMenuController />
                </div>
                <NavLink
                    to={DASHBOARD_ROUTE}
                    className={({isActive}) => classNames('bottom-nav-item', isActive && 'active')}
                    onClick={onClickTab(DASHBOARD_ROUTE)}
                >
                    <div className='bottom-nav-icon-wrapper'>
                        <Icon icon='lucide:house' className='bottom-nav-icon' />
                    </div>
                    <span className='bottom-nav-label'>{t('menu.home', 'Overzicht')}</span>
                </NavLink>
                <NavLink
                    to={ORDERS_ROUTE}
                    className={({isActive}) => classNames('bottom-nav-item', isActive && 'active')}
                    onClick={onClickTab(ORDERS_ROUTE)}
                >
                    <div className='bottom-nav-icon-wrapper'>
                        <Icon icon={tileIcon} className='bottom-nav-icon' />
                        {/* PWA-23: Orders unread badge hidden for v1 (Levi: the "4" was unclear and
                            tapping it led nowhere that explained it; revisit in v2).
                            TODO: uncomment this counter once a dedicated notifications LIST exists
                            for it to point at — then the badge has a meaningful destination. */}
                        {/* {renderBadge(ORDERS_ROUTE)} */}
                    </div>
                    <span className='bottom-nav-label'>Orders</span>
                </NavLink>
                <NavLink
                    to={NOTIFICATIONS_ROUTE}
                    className={({isActive}) => classNames('bottom-nav-item', isActive && 'active')}
                    onClick={onClickTab(NOTIFICATIONS_ROUTE)}
                >
                    <div className='bottom-nav-icon-wrapper'>
                        <Icon icon={Bell} className='bottom-nav-icon' />
                        {renderBadge(NOTIFICATIONS_ROUTE)}
                    </div>
                    <span className='bottom-nav-label'>Notif.</span>
                </NavLink>
                <NavLink
                    to={REVIEW_ANALYTICS_ROUTE}
                    className={({isActive}) => classNames('bottom-nav-item', isActive && 'active')}
                    onClick={onClickTab(REVIEW_ANALYTICS_ROUTE)}
                >
                    <div className='bottom-nav-icon-wrapper'>
                        <Icon icon={starIcon} className='bottom-nav-icon' />
                        {renderBadge(REVIEW_ANALYTICS_ROUTE)}
                    </div>
                    <span className='bottom-nav-label'>Reviews</span>
                </NavLink>
                <div className={classNames('bottom-nav-item', moreOpen && 'active')} onClick={toggleMore}>
                    <div className='bottom-nav-icon-wrapper'>
                        <Icon icon={userCircle} className='bottom-nav-icon' />
                    </div>
                    <span className='bottom-nav-label'>Account</span>
                </div>
            </nav>
            <FeedbackDialogController active={feedbackOpen} onClose={onCloseFeedback} />
        </>
    );
}
